import React, { useSyncExternalStore } from 'react';
import styled from 'styled-components';
import { NARROW } from '@/breakpoints.js';
import { answerConsent, getConsent, subscribeConsent } from '@/analytics.js';

// Asks once whether the visitor's sessions may be recorded with cookies (src/analytics.ts). Small
// and out of the way, over the bottom corner, on the home page and in the app alike: everything
// works without an answer, which counts as no. `hidden` puts it away for a while, as a playing
// slideshow does, and it comes back after if there is still no answer.

const Banner = styled.div`
	position: fixed;
	inset-block-end: 1em;
	inset-inline-end: 1em;
	z-index: 20;
	max-width: 22em;
	box-sizing: border-box;
	padding: 0.7em 0.9em;
	display: flex;
	align-items: center;
	gap: 0.75em;
	border-radius: 0.75em;
	background: rgba(255, 255, 255, 0.85);
	-webkit-backdrop-filter: blur(16px) saturate(180%);
	backdrop-filter: blur(16px) saturate(180%);
	border: 1px solid rgba(255, 255, 255, 0.7);
	box-shadow: 0 0.4em 1.2em rgba(0, 0, 0, 0.18);
	color: #222;
	font-size: 0.8rem;
	line-height: 1.35;

	p {
		margin: 0;
	}

	${NARROW} {
		inset-inline: 0.5em;
		inset-block-end: 0.5em;
		max-width: none;
	}
`;

const Buttons = styled.div`
	display: flex;
	gap: 0.4em;
	flex-shrink: 0;
`;

const Button = styled.button<{ $primary?: boolean }>`
	padding: 0.4em 0.8em;
	border-radius: 999px;
	border: 1px solid ${({ $primary }) => ($primary ? '#17171a' : '#0003')};
	background: ${({ $primary }) => ($primary ? '#17171a' : 'transparent')};
	color: ${({ $primary }) => ($primary ? '#fff' : '#222')};
	font: inherit;
	font-weight: 600;
	cursor: pointer;
	white-space: nowrap;

	&:focus-visible {
		outline: 2px solid #17171a;
		outline-offset: 2px;
	}
`;

export const ConsentBanner = ({ hidden = false }: { hidden?: boolean }) => {
	const consent = useSyncExternalStore(subscribeConsent, getConsent, () => 'unknown');
	if (consent !== 'pending' || hidden) return null;

	return (
		<Banner role="region" aria-label="Cookies">
			<p>May we use cookies to improve All2Scale?</p>
			<Buttons>
				<Button type="button" onClick={() => answerConsent(false)}>
					No
				</Button>
				<Button type="button" $primary onClick={() => answerConsent(true)}>
					Yes
				</Button>
			</Buttons>
		</Banner>
	);
};
